import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useInventoryVendorListSearchAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryVendorListSearchAPI";
import { usePurchaseOrderVendorBranchAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePurchaseOrderVendorBranchAPI";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useProductVeticCategoryListAPI } from "@/app/hooks/api_hooks/Inventory/useProductVeticCategoryListAPI";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { CLINIC_DETAILS_EDITABLE_STATUSES_FOR_PO, VENDOR_EDITABLE_STATUSES_FOR_PO } from "../../constants";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { FieldError, UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router";
import { IInventoryPurchaseOrderFormFields } from "..";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

interface IPrimaryPurchaseInfoFormFieldsProps {
  formHook: UseFormReturn<IInventoryPurchaseOrderFormFields, any>;
  onSubmit?: () => void;
  onCopyPreviousPOClick?: () => void;
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "CLINIC",
  },
];

const PrimaryPurchaseInfoFormFields: React.FC<IPrimaryPurchaseInfoFormFieldsProps> = ({
  formHook,
  onSubmit,
  onCopyPreviousPOClick,
}) => {
  const { getRouteKey } = useRouter();
  const { fieldDOBFormat } = useStringConstants();
  const userPermissions = useAppSelector((state) => state.auth?.userPermissions);
  const { purchaseOrderDetails: PODetails } = useRouteInfo(getRouteKey("INVENTORY", "id"))?.routeState
    ?.state as IInventoryState;
  const [editPermission, setEditPermission] = useState<boolean>(false);
  const navigate = useNavigate();
  const { execute: fetcClinicList, clinicList } = useClinicTamsFilterAPI();
  const { purchaseOrderVendorBranch, execute: fetchVendorsBranches } = usePurchaseOrderVendorBranchAPI();
  const { execute: searchInSection } = useInventoryVendorListSearchAPI();
  // RBAC: PO_CREATE_ACTION — legacy roles: clinic_ops_bluecity_PO, central_ops, Category_Ops
  const isCentralOps = useFeatureAccess("PO_CREATE_ACTION");
  const { execute: fetcVeticCaategory } = useProductVeticCategoryListAPI();
  const { execute: fetchCategory } = useCouponCategoryAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const purchaseOrderVendorBranchItem = useSelectMenuReducer(purchaseOrderVendorBranch, "branch_name", "branch_id", {});


  const isPrimarySectionEditable =
    PODetails?.po_status === "pre_draft" ||
    PODetails?.po_status === "draft" ||
    PODetails?.po_status === "pending_invoice_verification";
  const isExpectedDeliveryDateDisabled =
    PODetails?.po_status !== "pre_draft" && (formHook.watch("product_info")?.length || 0) > 0;
  const currentPOStatus = (PODetails?.po_status || "") as string;
  const isClinicDetailsEditable = CLINIC_DETAILS_EDITABLE_STATUSES_FOR_PO.includes(currentPOStatus);
  const isVendorDetailsEditable = VENDOR_EDITABLE_STATUSES_FOR_PO.includes(currentPOStatus);

  useEffect(() => {
    if (!userPermissions || !PODetails) return;
    if (userPermissions && isCentralOps) {
      setEditPermission(true);
    } else {
      setEditPermission(false);
    }
  }, [PODetails, userPermissions]);

  useEffect(() => {
    const vendorId = formHook.getValues("primary_purchase_info.vendor_details.vendor_id");
    if (vendorId) {
      fetchVendorsBranches(vendorId);
    }
  }, [formHook.watch("primary_purchase_info.vendor_details.vendor_id")]);

  useEffect(() => {
    const selectedBranchId = formHook.getValues("primary_purchase_info.vendor_details.branch_id");
    const selectedBranchName = formHook.getValues("primary_purchase_info.vendor_details.branch_name");

    if (selectedBranchId || !selectedBranchName || !purchaseOrderVendorBranchItem?.length) return;

    const matchedBranch = purchaseOrderVendorBranchItem.find(
      (branch) => branch?.title?.trim()?.toLowerCase() === selectedBranchName?.trim()?.toLowerCase(),
    );

    if (matchedBranch?.id) {
      formHook.setValue("primary_purchase_info.vendor_details.branch_id", matchedBranch.id);
    }
  }, [purchaseOrderVendorBranchItem, formHook.watch("primary_purchase_info.vendor_details.branch_name")]);

  useEffect(() => {
    const selectedType = formHook.watch("primary_purchase_info.type");
    if (selectedType) {
      if (selectedType === "TAMS") {
        fetcClinicList("", "TAMS");
      } else if (selectedType === "CLINIC") {
        fetcClinicList("", "CLINIC");
      }
    }
  }, [formHook.watch("primary_purchase_info.type")]);

  useEffect(() => {
    fetcVeticCaategory();
    fetchCategory();
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center">
        <h2 className="mt-3 font-semibold text-sm text-[#1F1F23]">Primary Purchase Information</h2>
        <div className="flex items-center gap-2">
          {isCentralOps && PODetails?.po_status === "pre_draft" && (
            <button
              type="button"
              onClick={() => onCopyPreviousPOClick?.()}
              className="mt-2 inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200"
            >
              Copy Previous PO
            </button>
          )}
          {isCentralOps && PODetails?.po_status !== "sent_to_finance" && PODetails?.po_status !== "pre_draft" && (
            <button
              onClick={() => {
                const branchId = formHook.getValues("primary_purchase_info.vendor_details.branch_id");
                const selectedBranch = purchaseOrderVendorBranch.find((branch) => branch.branch_id === branchId);
                if (selectedBranch?.branch_name) {
                  formHook.setValue("primary_purchase_info.vendor_details.branch_name", selectedBranch.branch_name);
                }
                onSubmit(PODetails?.po_status);
              }}
              className="mt-2 inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
              type="button"
            >
              Update PO
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-6 border-gray-200 mt-4 pb-5 border-b">
        <div className="flex items-center">
          <div className="w-2/12">
            <p>
              <MandatoryLabel label="Clinic/Tams" isMandatory />
            </p>
          </div>
          <div className="w-2/6 mt-5">
            {editPermission && isClinicDetailsEditable ? (
              <div>
                <SearchableSelectMenu
                  errorMessages={[{ type: "required", message: "Clinic/Tams is required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("primary_purchase_info.type", item.title);
                    }
                  }}
                  fieldError={(formHook?.formState?.errors?.primary_purchase_info?.type as FieldError) ?? null}
                  register={formHook.register("primary_purchase_info.type", { required: true })}
                  selectItems={types}
                  placeholder="Select Type"
                  showTooltips={true}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={types?.filter((oc) => oc.title === formHook.watch("primary_purchase_info.type"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                />
              </div>
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">{PODetails?.primary_purchase_info?.type}</p>
            )}
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p>
              <MandatoryLabel label="Vendor" isMandatory />
            </p>
          </div>

          <div className="w-2/6">
            {editPermission && isVendorDetailsEditable ? (
              <div>
                <SearchableSelectMenu
                  errorMessages={[
                    {
                      type: "required",
                      message: "Vendor Name Is Required",
                    },
                  ]}
                  placeholder="Search & Select Vendor "
                  fieldError={formHook?.formState?.errors?.primary_purchase_info?.vendor_details?.vendor_id ?? null}
                  register={formHook.register("primary_purchase_info.vendor_details.vendor_id", {
                    required: "Vendor Name Is Required",
                  })}
                  onSelectItem={(item) => {
                    formHook.setValue("primary_purchase_info.vendor_details.vendor_name", item.title);
                    formHook.setValue("primary_purchase_info.vendor_details.vendor_id", item.id);
                    formHook.clearErrors([
                      "primary_purchase_info.vendor_details.vendor_id",
                      "primary_purchase_info.vendor_details.vendor_name",
                    ]);
                  }}
                  withAPISearch={{
                    apiCallback: (searchID) => searchInSection(searchID, "PO_PAGE_SEARCH"),
                    reducerIdKey: "vendor_id",
                    reducerTitleKey: "trade_name",
                  }}
                  defaultSelectedItemForAPISearch={{
                    id: formHook.watch("primary_purchase_info.vendor_details.vendor_id"),
                    title: formHook.watch("primary_purchase_info.vendor_details.vendor_name"),
                  }}
                  showDropdownIcon
                />
              </div>
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">
                {PODetails?.primary_purchase_info?.vendor_details?.vendor_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center w-full">
          <div className="w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">
              Vendor Branch <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="w-2/6">
            {editPermission && isVendorDetailsEditable ? (
              <>
                <SearchableSelectMenu
                  errorMessages={[{ type: "required", message: "Vendor Branch is required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("primary_purchase_info.vendor_details.branch_name", item.title);
                      formHook.setValue("primary_purchase_info.vendor_details.branch_id", item.id);
                      formHook.clearErrors([
                        "primary_purchase_info.vendor_details.branch_id",
                        "primary_purchase_info.vendor_details.branch_name",
                      ]);
                    }
                  }}
                  fieldError={formHook?.formState?.errors?.primary_purchase_info?.vendor_details?.branch_id ?? null}
                  register={formHook.register("primary_purchase_info.vendor_details.branch_id", { required: true })}
                  selectItems={purchaseOrderVendorBranchItem}
                  placeholder="Select Vendor Branch"
                  showTooltips={false}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={
                    purchaseOrderVendorBranchItem.filter(
                      (oc) => oc.id === formHook.watch("primary_purchase_info.vendor_details.branch_id"),
                    )[0] || {
                      id:
                        formHook.watch("primary_purchase_info.vendor_details.branch_id") ||
                        formHook.watch("primary_purchase_info.vendor_details.branch_name"),
                      title: formHook.watch("primary_purchase_info.vendor_details.branch_name"),
                    }
                  }
                  // disabled={formHook.watch("product_info")?.length > 0}
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </>
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">
                {PODetails?.primary_purchase_info?.vendor_details?.branch_name}
              </p>
            )}
          </div>
          <p className="pl-3">
            {purchaseOrderVendorBranch[0]?.billing_address?.pincode === null && editPermission && (
              <p className="py-2 text-red-400 text-[10px] sm:text-xs 2xl:text-sm">
                Please Update the vendor details missing pincode or branch{" "}
                <a
                  className="text-blue-500 cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/app/inventory/vendor-add?vendor_id=${formHook.getValues("primary_purchase_info.vendor_details.vendor_id")}`,
                    )
                  }
                >
                  Edit
                </a>
              </p>
            )}
            {editPermission &&
              isVendorDetailsEditable &&
              formHook.getValues("primary_purchase_info.vendor_details.vendor_id") && (
                <p className="py-2 text-[10px] sm:text-xs 2xl:text-sm">
                  <a
                    className="text-blue-500 cursor-pointer"
                    onClick={() =>
                      window.open(
                        `/app/inventory/vendor-add?vendor_id=${formHook.getValues("primary_purchase_info.vendor_details.vendor_id")}`,
                        "_blank",
                      )
                    }
                  >
                    Edit Vendor/Branch Address & GST
                  </a>
                </p>
              )}
          </p>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">
              Bill To <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="w-2/6">
            {editPermission && isClinicDetailsEditable ? (
              <div>
                <SearchableSelectMenu
                  errorMessages={[
                    { type: "required", message: "Please select bill to" },
                    { type: "validate", message: "GSTN not configured! Please select another clinic." },
                  ]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue(
                        "primary_purchase_info.clinic_details.bill_to_clinic_name",
                        item.title.replace(item.state.gstin, "").trim(),
                      );
                      formHook.setValue("primary_purchase_info.clinic_details.bill_to_clinic_id", item.id);
                      formHook.setValue("primary_purchase_info.clinic_details.bill_to_clinic_gstin", item.state.gstin);
                      if (item?.state?.gstin) {
                        formHook.clearErrors([
                          "primary_purchase_info.clinic_details.bill_to_clinic_id",
                          "primary_purchase_info.clinic_details.bill_to_clinic_name",
                        ]);
                      } else {
                        formHook.setError("primary_purchase_info.clinic_details.bill_to_clinic_id", {
                          type: "validate",
                          message: "",
                        });
                      }
                    }
                  }}
                  fieldError={
                    formHook?.formState?.errors?.primary_purchase_info?.clinic_details?.bill_to_clinic_id ?? null
                  }
                  register={formHook.register("primary_purchase_info.clinic_details.bill_to_clinic_id", {
                    required: "Please select bill to",
                  })}
                  selectItems={clinicsOrgItem}
                  placeholder="Select Clinic"
                  showTooltips={false}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={
                    clinicsOrgItem.filter(
                      (oc) => oc.id === formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_id"),
                    )[0] || {
                      id:
                        formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_id") ||
                        formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_name"),
                      title: formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_name"),
                    }
                  }
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">
                {PODetails?.primary_purchase_info?.clinic_details?.bill_to_clinic_name}
              </p>
            )}
          </div>
          <p className="pl-3">
            {formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_gstin") && (
              <p className="mt-2 text-gray-500 text-[10px] sm:text-xs 2xl:text-sm">
                Clinic GSTIN : {formHook.watch("primary_purchase_info.clinic_details.bill_to_clinic_gstin")}
              </p>
            )}
          </p>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">
              Ship To <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="w-2/6">
            {editPermission && isClinicDetailsEditable ? (
              <SearchableSelectMenu
                errorMessages={[{ type: "required", message: "Please select ship to" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("primary_purchase_info.clinic_details.ship_to_clinic_name", item.title);
                    formHook.setValue("primary_purchase_info.clinic_details.ship_to_clinic_id", item.id);
                    formHook.clearErrors([
                      "primary_purchase_info.clinic_details.ship_to_clinic_id",
                      "primary_purchase_info.clinic_details.ship_to_clinic_name",
                    ]);
                  }
                }}
                fieldError={
                  formHook?.formState?.errors?.primary_purchase_info?.clinic_details?.ship_to_clinic_id ?? null
                }
                register={formHook.register("primary_purchase_info.clinic_details.ship_to_clinic_id", {
                  required: true,
                })}
                selectItems={clinicsOrgItem}
                placeholder="Select Clinic"
                showTooltips={false}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={
                  clinicsOrgItem.filter(
                    (oc) => oc.id === formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_id"),
                  )[0] || {
                    id:
                      formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_id") ||
                      formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_name"),
                    title: formHook.watch("primary_purchase_info.clinic_details.ship_to_clinic_name"),
                  }
                }
                className="text-gray-800"
                containerClassName="w-full"
              />
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">
                {PODetails?.primary_purchase_info?.clinic_details?.ship_to_clinic_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">PO Number</p>
          </div>
          <div className=" w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">{PODetails?.po_number}</p>
          </div>
        </div>
        <div className="flex items-center">
          <div className="w-2/12">
            <p className="text-[10px] sm:text-xs 2xl:text-sm">
              Expected Delivery Date <span className="text-red-400">*</span>
            </p>
          </div>
          <div className="w-48">
            {editPermission && isPrimarySectionEditable ? (
              <PWLCalendarComponent
                showFutureYears
                allowPastDaySelection={false}
                errorMessages={[{ message: "Date is required", type: "required" }]}
                register={formHook.register("primary_purchase_info.delivery_date", {
                  required: true,
                })}
                disableDatesBeforeDate={format(new Date(Date.now()), fieldDOBFormat)}
                fieldError={formHook?.formState?.errors?.primary_purchase_info?.delivery_date ?? null}
                onSelectDate={(date) => {
                  formHook.setValue("primary_purchase_info.delivery_date", format(date, fieldDOBFormat));
                  formHook.clearErrors("primary_purchase_info.delivery_date");
                }}
                defaultSelectedDate={formHook.getValues("primary_purchase_info.delivery_date")}
                className="w-full"
              />
            ) : (
              <p className="text-[10px] sm:text-xs 2xl:text-sm">{PODetails?.primary_purchase_info?.delivery_date}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryPurchaseInfoFormFields;
